import { appState, gameState, runtimeState, yutThrowState } from "./state.js";

const RUNTIME_ENDPOINT = "/api/runtime";
const SESSION_START_ENDPOINT = "/api/session/start";
const ACTION_ENDPOINT = "/api/action";
const MOCK_UPDATE_ENDPOINT = "/web-mini-game/update-asset";
const MOCK_DEBUG_SET_ENDPOINT = "/api/mock/csharp/debug/set";
const MOCK_DEBUG_RESET_ENDPOINT = "/api/mock/csharp/debug/reset";
const GAME_KEY = "yutnori";

function isClientApiDebugEnabled() {
  return runtimeState.clientApiDebug === true;
}

function logClientApiDebug(message, context = null) {
  if (!isClientApiDebugEnabled()) {
    return;
  }
  if (context) {
    console.log(`[client-api] ${message}`, context);
  } else {
    console.log(`[client-api] ${message}`);
  }
}

function logClientApiError(message, context = null) {
  if (context) {
    console.error(`[client-api] ${message}`, context);
  } else {
    console.error(`[client-api] ${message}`);
  }
}

async function requestJson(endpoint, options = {}) {
  const method = options.method || "GET";
  const payload = options.payload ?? null;
  const label = options.label || endpoint;
  const init = {
    method,
    headers: payload ? { "Content-Type": "application/json" } : undefined,
    body: payload ? JSON.stringify(payload) : undefined,
    cache: options.cache,
  };
  logClientApiDebug(`${label} request`, { endpoint, method, payload });
  try {
    const response = await fetch(endpoint, init);
    const text = await response.text();
    let data = null;
    if (text) {
      try {
        data = JSON.parse(text);
      } catch (err) {
        data = null;
      }
    }
    if (!response.ok) {
      logClientApiError(`${label} http error`, {
        endpoint,
        method,
        status: response.status,
        payload,
        body: text,
      });
      return null;
    }
    if (data && data.ok === false) {
      logClientApiError(`${label} response error`, {
        endpoint,
        method,
        status: response.status,
        payload,
        data,
      });
    }
    logClientApiDebug(`${label} response`, { endpoint, status: response.status, data });
    return data;
  } catch (err) {
    logClientApiError(`${label} request failed`, {
      endpoint,
      method,
      payload,
      error: String(err),
    });
    return null;
  }
}

function toSafeInt(value, fallback = 0) {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return Math.max(0, Math.round(fallback));
  }
  return Math.max(0, Math.round(num));
}

export function getTicketRemaining() {
  return Math.max(0, Math.round(yutThrowState.total - yutThrowState.used));
}

export function applyEconomySnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== "object") {
    return null;
  }
  if (snapshot.red_bag != null) {
    gameState.score = toSafeInt(snapshot.red_bag, gameState.score);
  }
  if (snapshot.blue_bag != null) {
    gameState.blueScore = toSafeInt(snapshot.blue_bag, gameState.blueScore);
  }
  if (snapshot.ticket_count != null) {
    const used = toSafeInt(yutThrowState.used, 0);
    const remaining = toSafeInt(snapshot.ticket_count, 0);
    yutThrowState.total = remaining + used;
  }
  appState.onEconomySync?.();
  appState.requestDraw?.();
  return snapshot;
}

export async function fetchRuntimeConfig() {
  const payload = await requestJson(RUNTIME_ENDPOINT, {
    label: "runtime",
    method: "GET",
    cache: "no-store",
  });
  if (!payload) {
    runtimeState.debugAllowed = runtimeState.unity !== "build";
    runtimeState.clientApiDebug = false;
    return null;
  }
  runtimeState.debugAllowed = payload && payload.debugAllowed !== false;
  runtimeState.clientApiDebug = payload && payload.clientApiDebug === true;
  return payload;
}

export async function startServerSession() {
  const payload = {
    session_key: runtimeState.sessionKey || null,
    unity: runtimeState.unity || null,
  };
  const data = await requestJson(SESSION_START_ENDPOINT, {
    label: "session/start",
    method: "POST",
    payload,
  });
  if (data && data.ok && data.session_id) {
    runtimeState.sessionId = data.session_id;
  }
  return data;
}

export async function sendServerAction(action) {
  if (!runtimeState.sessionId) {
    return null;
  }
  const payload = {
    session_id: runtimeState.sessionId,
    action: action || null,
  };
  return requestJson(ACTION_ENDPOINT, {
    label: "action",
    method: "POST",
    payload,
  });
}

export async function applyEconomyDelta(delta, options = null) {
  if (!runtimeState.useStub) {
    return null;
  }
  if (!delta || typeof delta !== "object") {
    return null;
  }
  const applySnapshot = options?.applySnapshot !== false;
  const ticketRaw = delta.ticketCount != null ? delta.ticketCount : delta.ticket_count;
  const redRaw = delta.redBag != null ? delta.redBag : delta.red_bag;
  const blueRaw = delta.blueBag != null ? delta.blueBag : delta.blue_bag;
  const payload = {
    sessionKey: runtimeState.sessionKey || null,
    game: GAME_KEY,
    delta: {
      ticketCount: ticketRaw,
      redBag: redRaw,
      blueBag: blueRaw,
    },
  };
  const data = await requestJson(MOCK_UPDATE_ENDPOINT, {
    label: "mock/update",
    method: "POST",
    payload,
  });
  if (applySnapshot && data && data.ok) {
    const res = data.res || data.result || null;
    if (res) {
      const snapshot = {};
      const hasTicket =
        Object.prototype.hasOwnProperty.call(delta, "ticket_count") ||
        Object.prototype.hasOwnProperty.call(delta, "ticketCount");
      const hasRed =
        Object.prototype.hasOwnProperty.call(delta, "red_bag") ||
        Object.prototype.hasOwnProperty.call(delta, "redBag");
      const hasBlue =
        Object.prototype.hasOwnProperty.call(delta, "blue_bag") ||
        Object.prototype.hasOwnProperty.call(delta, "blueBag");
      if (hasTicket) {
        snapshot.ticket_count = res.ticketCount != null ? res.ticketCount : res.ticket_count;
      }
      if (hasRed) {
        snapshot.red_bag = res.redBag != null ? res.redBag : res.red_bag;
      }
      if (hasBlue) {
        snapshot.blue_bag = res.blueBag != null ? res.blueBag : res.blue_bag;
      }
      applyEconomySnapshot(snapshot);
    }
  }
  return data;
}

export async function setMockEconomy(values) {
  if (!runtimeState.useStub) {
    return null;
  }
  const payload = {
    sessionKey: runtimeState.sessionKey || null,
    ticketCount: values?.ticket_count,
    redBag: values?.red_bag,
    blueBag: values?.blue_bag,
  };
  const data = await requestJson(MOCK_DEBUG_SET_ENDPOINT, {
    label: "mock/debug/set",
    method: "POST",
    payload,
  });
  const res = data && data.ok ? data.res || data.result : null;
  if (res) {
    applyEconomySnapshot({
      ticket_count: res.ticketCount != null ? res.ticketCount : res.ticket_count,
      red_bag: res.redBag != null ? res.redBag : res.red_bag,
      blue_bag: res.blueBag != null ? res.blueBag : res.blue_bag,
    });
  }
  return data;
}

export async function resetMockEconomy() {
  if (!runtimeState.useStub) {
    return null;
  }
  const payload = {
    sessionKey: runtimeState.sessionKey || null,
  };
  const data = await requestJson(MOCK_DEBUG_RESET_ENDPOINT, {
    label: "mock/debug/reset",
    method: "POST",
    payload,
  });
  const res = data && data.ok ? data.res || data.result : null;
  if (res) {
    applyEconomySnapshot({
      ticket_count: res.ticketCount != null ? res.ticketCount : res.ticket_count,
      red_bag: res.redBag != null ? res.redBag : res.red_bag,
      blue_bag: res.blueBag != null ? res.blueBag : res.blue_bag,
    });
  }
  return data;
}
